import {
  normalizeAssistantRouteIdentity,
  routeMatchesAnyAssistantPattern,
  routeMatchesAssistantPattern,
  stripAssistantRoutePatternQualifier,
} from './route-matching';
import {
  resolveAssistantRouteFamily,
  type AssistantRouteFamilyId,
} from './route-family-registry';
import {
  buildAssistantPromptMatrixScenarioKey,
  buildAssistantPromptMatrixSourceKey,
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SOURCE_ROWS,
} from './prompt-matrix-source';
import { hasExplicitSettingsSpecialistBinding } from './settings-specialist-intent';

export type AssistantCapabilityType = 'insight' | 'action';
export type AssistantCapabilityCatalogScope = 'route' | 'module' | 'staff-global';

export type AssistantModuleKey =
  | 'admissions'
  | 'financial-aid'
  | 'faculty'
  | 'settings'
  | 'students-families'
  | 'tuition'
  | 'attendance'
  | 'parent-portal'
  | 'general';

export type AssistantPersona =
  | 'staff'
  | 'admin'
  | 'owner'
  | 'editor'
  | 'viewer'
  | 'nurse'
  | 'parent'
  | 'super-administrator';

export type AssistantCapabilityActionKey =
  | 'settings.updateSchoolLogo'
  | 'settings.addDivision'
  | 'settings.uploadParentResource'
  | 'settings.editDivisionCapacity'
  | 'settings.addAddress'
  | 'settings.todoSpreadsheetExportBridge'
  | 'settings.inviteUser'
  | 'settings.changeUserRole'
  | 'settings.resendPendingInvite'
  | 'settings.assignDivisionSigner'
  | 'settings.filterStaffByRole'
  | 'settings.changeAdmissionsStatus'
  | 'settings.addTuitionRate'
  | 'settings.updateTuitionAmounts'
  | 'settings.addFeeType'
  | 'settings.toggleAdmissionsRequiredDocument'
  | 'settings.updateBudgetThresholds'
  | 'settings.setNextYearDates'
  | 'settings.changeLateFeeAmount'
  | 'settings.toggleFinancialAidRequiredDocument'
  | 'settings.updateScholarshipBudget';

export type AssistantCapabilityDefinition = {
  id: string;
  page: string;
  tab: string;
  sequence: number;
  title: string;
  type: AssistantCapabilityType;
  method?: string;
  analysisScope?: 'dataset' | 'focused';
  rationale: string;
  module: AssistantModuleKey;
  personas: AssistantPersona[];
  routePrefixes: string[];
  prompt: string;
  requiresConfirmation?: boolean;
  actionKey?: AssistantCapabilityActionKey;
  actionDefaults?: Record<string, unknown>;
};

export type AssistantCapabilityBindingValidationReasonCode =
  | 'aligned'
  | 'no-binding'
  | 'capability-not-found'
  | 'capability-route-mismatch';

export type AssistantCapabilityBindingValidationResult = {
  hasBinding: boolean;
  isValid: boolean;
  reasonCode: AssistantCapabilityBindingValidationReasonCode;
  routeModule: AssistantModuleKey;
  capability: AssistantCapabilityDefinition | null;
  scenarioKey: string | null;
  method: string | null;
  targetLabel: string | null;
  isSettingsSpecialist: boolean;
};

const STAFF_SETTINGS_PERSONAS: AssistantPersona[] = ['staff', 'admin', 'owner', 'super-administrator', 'editor'];
const STAFF_INSIGHT_PERSONAS: AssistantPersona[] = [
  'staff',
  'admin',
  'owner',
  'editor',
  'viewer',
  'nurse',
  'super-administrator',
];
const ATTENDANCE_STAFF_PERSONAS: AssistantPersona[] = ['staff', 'admin', 'owner', 'editor', 'super-administrator'];
const PARENT_PERSONAS: AssistantPersona[] = ['parent'];

const SETTINGS_GENERAL_ROUTE_PREFIXES = ['/school/settings/general'];
const SETTINGS_USERS_ROUTE_PREFIXES = ['/school/settings/users'];
const SETTINGS_ADMISSIONS_ROUTE_PREFIXES = ['/school/settings/admissions'];
const SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES = [
  '/school/settings/financialaid',
  '/school/settings/financial-aid',
];
const ADMISSIONS_DASHBOARD_PREFIXES = [
  'exact:/school/admissions',
  'exact:/school/admissions/dashboard',
];
const ADMISSIONS_DETAIL_PREFIXES = [
  '/school/admissions/children/',
  '/school/admissions/family/',
  '/school/admissions/documents/',
  '/school/admissions/notes/',
  '/school/admissions/tuition/',
];
const STUDENTS_FAMILIES_DASHBOARD_PREFIXES = [
  'exact:/school/students',
  'exact:/school/students/dashboard',
];
const STUDENTS_FAMILIES_CHILDREN_ROUTE_PATTERNS = [
  '/school/students/family/:familyId',
  '/school/students/family/:familyId/children',
];
const STUDENTS_FAMILIES_FAMILY_ROUTE_PATTERNS = [
  '/school/students/family/:familyId/family',
];
const STUDENTS_FAMILIES_TUITION_ROUTE_PATTERNS = [
  '/school/students/family/:familyId/tuition',
];
const STUDENTS_FAMILIES_DOCUMENTS_ROUTE_PATTERNS = [
  '/school/students/family/:familyId/documents',
];
const STUDENTS_FAMILIES_NOTES_ROUTE_PATTERNS = [
  '/school/students/family/:familyId/notes',
];
const FINANCIAL_AID_DASHBOARD_PREFIXES = [
  'exact:/school/financialaid',
  'exact:/school/financialaid/dashboard',
  'exact:/school/financial-aid',
  'exact:/school/financial-aid/dashboard',
  'exact:/school/financialaid/staff',
  'exact:/school/financial-aid/staff',
];
const FINANCIAL_AID_DETAIL_PREFIXES = [
  '/school/financialaid/staff/',
  '/school/financial-aid/staff/',
];
const PARENT_PORTAL_PREFIXES = [
  '/parent/portal',
  '/parent/portal/',
  '/parent/portal--new',
  '/parent/portal--new/',
  '/portal/family',
  '/portal/family/',
];
const ASSISTANT_HOME_ROUTE_PREFIXES = ['/school/assistant'];
const ATTENDANCE_OVERVIEW_ROUTE_PREFIXES = ['exact:/school/attendance'];
const ATTENDANCE_RECORDS_ROUTE_PREFIXES = ['/school/attendance/new-records'];
const ACADEMICS_SUMMARY_ROUTE_PREFIXES = ['exact:/school/academics/summary'];
const ACADEMICS_APPROVALS_ROUTE_PREFIXES = ['exact:/school/academics/approvals'];
const SUBSTITUTES_DASHBOARD_ROUTE_PREFIXES = ['exact:/school/substitutes'];
const SUBSTITUTES_PAYROLL_ROUTE_PREFIXES = ['exact:/school/substitutes/payroll'];
const FACULTY_DIRECTORY_ROUTE_PATTERNS = [
  'exact:/school/faculty',
  'exact:/school/faculty/directory',
  'exact:/bridged/admin/faculty',
  'exact:/bridged/admin/faculty/directory',
];
const FACULTY_PROFILE_ROUTE_PATTERNS = [
  '/school/faculty/:facultyId',
  '/school/faculty/profile/:facultyId',
  '/bridged/admin/faculty/:facultyId',
  '/bridged/admin/faculty/profile/:facultyId',
];
const FACULTY_TEACHING_ROUTE_PATTERNS = [
  '/school/faculty/:facultyId/teaching-assignment',
  '/school/faculty/profile/:facultyId/teaching',
  '/bridged/admin/faculty/:facultyId/teaching-assignment',
  '/bridged/admin/faculty/profile/:facultyId/teaching',
];
const FACULTY_NOTES_ROUTE_PATTERNS = [
  '/school/faculty/:facultyId/notes',
  '/school/faculty/profile/:facultyId/notes',
  '/bridged/admin/faculty/:facultyId/notes',
  '/bridged/admin/faculty/profile/:facultyId/notes',
];
const FACULTY_COMMUNICATIONS_ROUTE_PATTERNS = [
  '/school/faculty/:facultyId/communications',
  '/school/faculty/profile/:facultyId/communications',
  '/bridged/admin/faculty/:facultyId/communications',
  '/bridged/admin/faculty/profile/:facultyId/communications',
];
const FINANCE_OVERVIEW_ROUTE_PREFIXES = ['exact:/school/finance'];
const FINANCE_RECONCILIATION_ROUTE_PREFIXES = ['exact:/school/finance/reconciliation'];
const PAYROLL_DASHBOARD_ROUTE_PREFIXES = ['exact:/school/payroll'];
const PAYROLL_EMPLOYEE_DETAIL_ROUTE_PREFIXES = ['/school/payroll/employee/:employeeId'];
const CAMPS_DASHBOARD_ROUTE_PREFIXES = ['exact:/school/camps'];
const CAMPS_PROGRAM_DETAIL_ROUTE_PREFIXES = ['/school/camps/:campId'];
const DONATIONS_DASHBOARD_ROUTE_PREFIXES = ['exact:/school/donations'];
const DONATIONS_LEDGER_ROUTE_PREFIXES = ['exact:/school/donations/ledger'];
const TIP_A_TEACHER_ROUTE_PREFIXES = ['/school/tip-a-teacher'];
const TUITION_ACCOUNTS_ROUTE_PREFIXES = ['exact:/school/tuition'];
const TUITION_OPERATIONS_ROUTE_PREFIXES = ['exact:/administrator/tuition'];

const capability = (
  definition: AssistantCapabilityDefinition
): AssistantCapabilityDefinition => definition;

export const YSTNJ_ASSISTANT_CAPABILITY_MATRIX: AssistantCapabilityDefinition[] = [
  capability({
    id: 'assistant-home-estimate-enrollment-yield',
    page: 'Assistant',
    tab: 'Quick Actions',
    sequence: 1,
    title: 'Estimate enrollment yield',
    type: 'insight',
    method: 'Summarize current admissions pipeline signals and project likely enrollment yield for the current cycle.',
    rationale: 'Assistant home should open with a high-value cross-route workflow that matches the visual authority starter set.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ASSISTANT_HOME_ROUTE_PREFIXES,
    prompt: 'Estimate enrollment yield for the current cycle.',
  }),
  capability({
    id: 'assistant-home-identify-pipeline-bottlenecks',
    page: 'Assistant',
    tab: 'Quick Actions',
    sequence: 2,
    title: 'Identify pipeline bottlenecks',
    type: 'insight',
    method: 'Review cross-route admissions workflow pressure and explain where the current pipeline is slowing down.',
    rationale: 'The assistant home lane needs route-agnostic follow-up prompts instead of an empty generic shell.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ASSISTANT_HOME_ROUTE_PREFIXES,
    prompt: 'Identify the current pipeline bottlenecks and explain what needs attention.',
  }),
  capability({
    id: 'assistant-home-flag-at-risk-applicants',
    page: 'Assistant',
    tab: 'Quick Actions',
    sequence: 3,
    title: 'Flag at-risk applicants',
    type: 'insight',
    method: 'Surface applicants likely to miss deadlines, documents, or readiness gates from the current school workflow context.',
    rationale: 'At-risk review is part of the declared assistant-home authority quick-action set.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ASSISTANT_HOME_ROUTE_PREFIXES,
    prompt: 'Flag the applicants who are currently at risk and explain why.',
  }),
  capability({
    id: 'assistant-home-schedule-interview-batch',
    page: 'Assistant',
    tab: 'Quick Actions',
    sequence: 4,
    title: 'Schedule interview batch',
    type: 'action',
    method: 'Plan a route-aware interview batch and prepare the background workflow for follow-up scheduling.',
    rationale: 'The authority quick-action set includes an execution-oriented batch scheduling prompt, not just passive insights.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ASSISTANT_HOME_ROUTE_PREFIXES,
    prompt: 'Schedule an interview batch for the next set of ready applicants.',
  }),
  capability({
    id: 'settings-general-update-school-logo',
    page: 'Settings',
    tab: 'General',
    sequence: 1,
    title: 'Update school logo',
    type: 'action',
    method: 'settings.updateSchoolLogo',
    rationale: 'Logo upload is prominent and a common first-time setup task.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Update school logo to the latest approved brand asset.',
    requiresConfirmation: true,
    actionKey: 'settings.updateSchoolLogo',
  }),
  capability({
    id: 'settings-general-add-division',
    page: 'Settings',
    tab: 'General',
    sequence: 2,
    title: 'Add a division',
    type: 'action',
    method: 'settings.addDivision',
    rationale: 'Expanding grade levels or programs is a frequent config change.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Add a new school division for the upcoming cycle.',
    requiresConfirmation: true,
    actionKey: 'settings.addDivision',
  }),
  capability({
    id: 'settings-general-upload-parent-resource',
    page: 'Settings',
    tab: 'General',
    sequence: 3,
    title: 'Upload parent resource',
    type: 'action',
    method: 'settings.uploadParentResource',
    rationale: 'Handbooks/calendars rotate yearly and require updates.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Upload a new parent resource and publish it to the portal.',
    requiresConfirmation: true,
    actionKey: 'settings.uploadParentResource',
  }),
  capability({
    id: 'settings-general-edit-division-capacity',
    page: 'Settings',
    tab: 'General',
    sequence: 4,
    title: 'Edit division capacity',
    type: 'action',
    method: 'settings.editDivisionCapacity',
    rationale: 'Capacity adjustments follow enrollment projection changes.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Adjust division capacity for the next admissions cycle.',
    requiresConfirmation: true,
    actionKey: 'settings.editDivisionCapacity',
  }),
  capability({
    id: 'settings-general-add-address',
    page: 'Settings',
    tab: 'General',
    sequence: 5,
    title: 'Add an address',
    type: 'action',
    method: 'settings.addAddress',
    rationale: 'Multi-campus schools need additional locations registered.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Add an additional campus address to organization settings.',
    requiresConfirmation: true,
    actionKey: 'settings.addAddress',
  }),
  capability({
    id: 'settings-general-audit-branding-projection',
    page: 'Settings',
    tab: 'General',
    sequence: 6,
    title: 'Audit branding projection',
    type: 'insight',
    method: 'settings.reviewBrandProjection',
    analysisScope: 'focused',
    rationale: 'Branding and organization settings affect parent and public-facing surfaces well beyond the current route.',
    module: 'settings',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SETTINGS_GENERAL_ROUTE_PREFIXES,
    prompt: 'Audit branding projection from settings into staff, parent, and public surfaces. Flag any route or artifact where logo, contact, or resource presentation could drift.',
  }),
  capability({
    id: 'settings-users-invite-user',
    page: 'Settings',
    tab: 'Users',
    sequence: 1,
    title: 'Invite a user',
    type: 'action',
    method: 'settings.inviteUser',
    rationale: 'Directly maps to the primary CTA on the Users page.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Invite a new staff user with the correct default role.',
    requiresConfirmation: true,
    actionKey: 'settings.inviteUser',
  }),
  capability({
    id: 'settings-users-change-user-role',
    page: 'Settings',
    tab: 'Users',
    sequence: 2,
    title: "Change a user's role",
    type: 'action',
    method: 'settings.changeUserRole',
    rationale: 'Role reassignment is frequent and high impact.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Change a staff user role and persist permissions.',
    requiresConfirmation: true,
    actionKey: 'settings.changeUserRole',
  }),
  capability({
    id: 'settings-users-resend-pending-invite',
    page: 'Settings',
    tab: 'Users',
    sequence: 3,
    title: 'Resend pending invite',
    type: 'action',
    method: 'settings.resendPendingInvite',
    rationale: 'Supports users still in invited/pending state.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Resend invite to a pending user.',
    requiresConfirmation: true,
    actionKey: 'settings.resendPendingInvite',
  }),
  capability({
    id: 'settings-users-assign-division-signer',
    page: 'Settings',
    tab: 'Users',
    sequence: 4,
    title: 'Assign division signer',
    type: 'action',
    method: 'settings.assignDivisionSigner',
    rationale: 'Principals/signers must be configured per division.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Assign a division signer for enrollment approvals.',
    requiresConfirmation: true,
    actionKey: 'settings.assignDivisionSigner',
  }),
  capability({
    id: 'settings-users-filter-staff-by-role',
    page: 'Settings',
    tab: 'Users',
    sequence: 5,
    title: 'Filter staff by role',
    type: 'action',
    method: 'settings.filterStaffByRole',
    rationale: 'Role filtering is a frequent operational need as staff grows.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Filter staff list by role and focus the review queue.',
    requiresConfirmation: false,
    actionKey: 'settings.filterStaffByRole',
  }),
  capability({
    id: 'settings-users-audit-access-governance',
    page: 'Settings',
    tab: 'Users',
    sequence: 6,
    title: 'Audit access governance',
    type: 'insight',
    method: 'settings.reviewAccessGovernance',
    analysisScope: 'focused',
    rationale: 'User governance requires more than one button click because invites, roles, signers, and access-visible state can drift together.',
    module: 'settings',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SETTINGS_USERS_ROUTE_PREFIXES,
    prompt: 'Review invites, roles, signers, and access-visible state from this users route. Flag governance drift, pending invites, and any risky permission gaps.',
  }),
  capability({
    id: 'settings-admissions-change-admissions-status',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 1,
    title: 'Change admissions status',
    type: 'action',
    method: 'settings.changeAdmissionsStatus',
    rationale: 'Controls what families see on the parent portal.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Change the admissions cycle status for the current year.',
    requiresConfirmation: true,
    actionKey: 'settings.changeAdmissionsStatus',
  }),
  capability({
    id: 'settings-admissions-add-tuition-rate',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 2,
    title: 'Add a tuition rate',
    type: 'action',
    method: 'settings.addTuitionRate',
    rationale: 'New divisions and grades require tuition rate entries.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Add a tuition rate entry for a grade/division.',
    requiresConfirmation: true,
    actionKey: 'settings.addTuitionRate',
  }),
  capability({
    id: 'settings-admissions-update-tuition-amounts',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 3,
    title: 'Update tuition amounts',
    type: 'action',
    method: 'settings.updateTuitionAmounts',
    rationale: 'Tuition and fees shift year to year before cycle launch.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Apply tuition amount updates across existing rates.',
    requiresConfirmation: true,
    actionKey: 'settings.updateTuitionAmounts',
  }),
  capability({
    id: 'settings-admissions-add-fee-type',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 4,
    title: 'Add a fee type',
    type: 'action',
    method: 'settings.addFeeType',
    rationale: 'New annual fees are common and should be configurable.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Add a new admissions fee type.',
    requiresConfirmation: true,
    actionKey: 'settings.addFeeType',
  }),
  capability({
    id: 'settings-admissions-toggle-required-documents',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 5,
    title: 'Toggle required documents',
    type: 'action',
    method: 'settings.toggleAdmissionsRequiredDocument',
    rationale: 'Document requirements change by cycle and policy updates.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Toggle admissions required document rules.',
    requiresConfirmation: true,
    actionKey: 'settings.toggleAdmissionsRequiredDocument',
  }),
  capability({
    id: 'settings-admissions-explain-policy-impact',
    page: 'Settings',
    tab: 'Admissions',
    sequence: 6,
    title: 'Explain admissions policy impact',
    type: 'insight',
    method: 'settings.reviewAdmissionsPolicyImpact',
    analysisScope: 'focused',
    rationale: 'Admissions settings change what families experience across documents, cycle state, and tuition-related downstream behavior.',
    module: 'settings',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SETTINGS_ADMISSIONS_ROUTE_PREFIXES,
    prompt: 'Explain how current admissions settings affect required documents, cycle status, tuition configuration, and the downstream parent experience. Flag the riskiest setting.',
  }),
  capability({
    id: 'settings-financial-aid-update-budget-thresholds',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 1,
    title: 'Update budget thresholds',
    type: 'action',
    method: 'settings.updateBudgetThresholds',
    rationale: 'Warning/critical thresholds drive dashboard alerts.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Update financial aid warning and critical budget thresholds.',
    requiresConfirmation: true,
    actionKey: 'settings.updateBudgetThresholds',
  }),
  capability({
    id: 'settings-financial-aid-set-next-year-dates',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 2,
    title: "Set next year's dates",
    type: 'action',
    method: 'settings.setNextYearDates',
    rationale: 'Upcoming cycle dates are a recurring annual setup step.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Set next year financial-aid cycle dates.',
    requiresConfirmation: true,
    actionKey: 'settings.setNextYearDates',
  }),
  capability({
    id: 'settings-financial-aid-change-late-fee-amount',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 3,
    title: 'Change late fee amount',
    type: 'action',
    method: 'settings.changeLateFeeAmount',
    rationale: 'Late fee adjustments are common across cycles.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Adjust the late fee amount for financial aid submissions.',
    requiresConfirmation: true,
    actionKey: 'settings.changeLateFeeAmount',
  }),
  capability({
    id: 'settings-financial-aid-toggle-required-documents',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 4,
    title: 'Toggle required documents',
    type: 'action',
    method: 'settings.toggleFinancialAidRequiredDocument',
    rationale: 'Financial-aid document requirements need periodic tuning.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Toggle a required financial aid document requirement.',
    requiresConfirmation: true,
    actionKey: 'settings.toggleFinancialAidRequiredDocument',
  }),
  capability({
    id: 'settings-financial-aid-update-scholarship-budget',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 5,
    title: 'Update scholarship budget',
    type: 'action',
    method: 'settings.updateScholarshipBudget',
    rationale: 'Annual scholarship budget resets and requires revision.',
    module: 'settings',
    personas: STAFF_SETTINGS_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Update annual scholarship budget allocation.',
    requiresConfirmation: true,
    actionKey: 'settings.updateScholarshipBudget',
  }),
  capability({
    id: 'settings-financial-aid-review-policy-readiness',
    page: 'Settings',
    tab: 'Financial Aid',
    sequence: 6,
    title: 'Review aid policy readiness',
    type: 'insight',
    method: 'settings.reviewFinancialAidPolicyReadiness',
    analysisScope: 'focused',
    rationale: 'Financial-aid settings need one route-aware readiness summary spanning dates, thresholds, required docs, and import posture.',
    module: 'settings',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SETTINGS_FINANCIAL_AID_ROUTE_PREFIXES,
    prompt: 'Summarize deadlines, thresholds, required documents, notifications, and import readiness for financial aid settings. Flag the next risky misconfiguration.',
  }),
  capability({
    id: 'admissions-dashboard-identify-pipeline-bottlenecks',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Identify pipeline bottlenecks',
    type: 'insight',
    method: 'Count applicants per stage, calculate avg time-in-stage, compare conversions, and flag bottlenecks.',
    rationale: 'Reveals where funnel throughput is blocked.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Identify admissions pipeline bottlenecks by stage count, dwell time, and conversion rates.',
  }),
  capability({
    id: 'admissions-dashboard-estimate-enrollment-yield',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 2,
    title: 'Estimate enrollment yield',
    type: 'insight',
    method: 'Use historical decision-to-enrollment ratios to project enrolled headcount.',
    rationale: 'Supports forecasting and planning before final decisions.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Estimate enrollment yield using historical decision-to-enrollment conversion.',
  }),
  capability({
    id: 'admissions-dashboard-flag-at-risk-applicants',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 3,
    title: 'Flag at-risk applicants',
    type: 'insight',
    method: 'Score risk from time-in-stage, missing docs, and inactivity.',
    rationale: 'Prevents high-probability drop-off cases from being missed.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Flag at-risk applicants based on inactivity, missing docs, and stalled status.',
  }),
  capability({
    id: 'admissions-dashboard-forecast-capacity-by-division',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 4,
    title: 'Forecast capacity by division',
    type: 'insight',
    method: 'Cross-reference pipeline vs division capacity weighted by conversion probability.',
    rationale: 'Shows likely over/under-capacity before acceptance decisions.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Forecast projected division capacity fill rate using current pipeline probabilities.',
  }),
  capability({
    id: 'admissions-dashboard-summarize-weekly-changes',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 5,
    title: 'Summarize weekly changes',
    type: 'insight',
    method: 'Diff current board with prior snapshot for movement and stagnation.',
    rationale: 'Provides quick executive update on weekly admissions movement.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Summarize weekly admissions changes: new cards, stage changes, and stalled applications.',
  }),
  capability({
    id: 'admissions-dashboard-create-status-chart',
    page: 'Admissions',
    tab: 'Dashboard',
    sequence: 6,
    title: 'Create a bar chart by status',
    type: 'insight',
    method: 'Aggregate live admissions dashboard applications by status and package the result as a Recharts-ready chart artifact with inline preview support.',
    rationale: 'Operators need a first-class charting prompt that routes to the chart artifact workflow instead of falling back to chat-only explanations.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DASHBOARD_PREFIXES,
    prompt: 'Make a simple bar chart categorized by status using a Recharts diagram.',
  }),
  capability({
    id: 'admissions-family-detail-summarize-readiness',
    page: 'Admissions',
    tab: 'Family Detail',
    sequence: 1,
    title: 'Summarize application readiness',
    type: 'insight',
    method: 'Aggregate docs, schedules, notes, and milestones into readiness score.',
    rationale: 'Turns multi-tab complexity into a single actionable readiness signal.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DETAIL_PREFIXES,
    prompt: 'Summarize this family application readiness across documents, scheduling, and notes.',
  }),
  capability({
    id: 'admissions-family-detail-flag-overdue-follow-ups',
    page: 'Admissions',
    tab: 'Family Detail',
    sequence: 2,
    title: 'Flag overdue follow-ups',
    type: 'insight',
    method: 'Parse note commitments and compare due dates against current date.',
    rationale: 'Ensures verbal commitments are tracked and not silently missed.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DETAIL_PREFIXES,
    prompt: 'Flag overdue follow-ups from notes and commitments for this family.',
  }),
  capability({
    id: 'admissions-family-detail-assess-sibling-coordination',
    page: 'Admissions',
    tab: 'Family Detail',
    sequence: 3,
    title: 'Assess sibling coordination',
    type: 'insight',
    method: 'Compare sibling progress, docs, and scheduling divergence.',
    rationale: 'Highlights inconsistent sibling progression that impacts final decisions.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DETAIL_PREFIXES,
    prompt: 'Assess sibling coordination and flag divergence across sibling application states.',
  }),
  capability({
    id: 'admissions-family-detail-review-medical-accommodations',
    page: 'Admissions',
    tab: 'Family Detail',
    sequence: 4,
    title: 'Review medical accommodations',
    type: 'insight',
    method: 'Scan medical fields for accommodation needs and prep checklist.',
    rationale: 'Prepares receiving divisions before placement decisions.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DETAIL_PREFIXES,
    prompt: 'Review medical accommodations and generate preparation checklist by division.',
  }),
  capability({
    id: 'admissions-family-detail-estimate-time-in-pipeline',
    page: 'Admissions',
    tab: 'Family Detail',
    sequence: 5,
    title: 'Estimate time in pipeline',
    type: 'insight',
    method: 'Benchmark application duration against historical median cycle.',
    rationale: 'Identifies outlier cases that need intervention.',
    module: 'admissions',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ADMISSIONS_DETAIL_PREFIXES,
    prompt: 'Estimate time in pipeline and flag outlier duration for this application.',
  }),
  capability({
    id: 'students-families-dashboard-guide-add-student-record',
    page: 'Students & Families',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Guide me through adding a student record',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Add-student workflow is a first-arrival task on the roster dashboard and should be guided from the assistant.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DASHBOARD_PREFIXES,
    prompt: 'Guide me through adding a student record from this dashboard, including where to start, what to verify, and how to confirm the save.',
  }),
  capability({
    id: 'students-families-dashboard-guide-add-family-record',
    page: 'Students & Families',
    tab: 'Dashboard',
    sequence: 2,
    title: 'Guide me through adding a family record',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Staff commonly starts new household onboarding from the dashboard and needs route-aware guidance.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DASHBOARD_PREFIXES,
    prompt: 'Guide me through adding a family record from this dashboard, including which details should be completed before leaving the page.',
  }),
  capability({
    id: 'students-families-dashboard-choose-roster-view',
    page: 'Students & Families',
    tab: 'Dashboard',
    sequence: 3,
    title: 'Help me choose the right roster view',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Operators switch between student and family views constantly and benefit from explicit route-aware guidance.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DASHBOARD_PREFIXES,
    prompt: 'Help me choose the right roster view for this task and explain when to stay in students view versus family view.',
  }),
  capability({
    id: 'students-families-dashboard-identify-follow-up-families',
    page: 'Students & Families',
    tab: 'Dashboard',
    sequence: 4,
    title: 'Identify families needing follow-up',
    type: 'insight',
    analysisScope: 'dataset',
    rationale: 'Staff needs a fast triage view for families blocked on docs, outreach, or billing follow-up.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DASHBOARD_PREFIXES,
    prompt: 'Identify families that need staff follow-up based on missing documents, stale communications, or overdue balance activity.',
  }),
  capability({
    id: 'students-families-dashboard-summarize-balance-risk',
    page: 'Students & Families',
    tab: 'Dashboard',
    sequence: 5,
    title: 'Summarize balance risk',
    type: 'insight',
    analysisScope: 'dataset',
    rationale: 'Open balances and payment activity are a core operational risk signal for staff review.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DASHBOARD_PREFIXES,
    prompt: 'Summarize family balance risk across the current roster using open balances and recent account activity.',
  }),
  capability({
    id: 'students-families-children-review-status-contact-and-schedule',
    page: 'Students & Families',
    tab: 'Children',
    sequence: 1,
    title: 'Review status, contact, and schedule follow-up',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'The children view is where staff triages status changes, outreach, and scheduling for the current family.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_CHILDREN_ROUTE_PATTERNS,
    prompt: 'Review this family for status, contact, and scheduling follow-up so the next staff action is obvious.',
  }),
  capability({
    id: 'students-families-family-summarize-profile',
    page: 'Students & Families',
    tab: 'Family',
    sequence: 1,
    title: 'Summarize this family profile',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'The family profile tab should expose a concise route-local summary before staff edits or saves household details.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_FAMILY_ROUTE_PATTERNS,
    prompt: 'Summarize this family profile and call out the most important profile fields, status signals, and follow-up risks.',
  }),
  capability({
    id: 'students-families-tuition-review-balance-and-payment-methods',
    page: 'Students & Families',
    tab: 'Tuition',
    sequence: 1,
    title: 'Review tuition balance and payment methods',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Tuition follow-up on family detail depends on the current ledger view and saved payment methods.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_TUITION_ROUTE_PATTERNS,
    prompt: 'Review this family tuition view for balance risk, recent payment activity, and payment-method follow-up.',
  }),
  capability({
    id: 'students-families-documents-review-missing-documents-and-follow-up',
    page: 'Students & Families',
    tab: 'Documents',
    sequence: 1,
    title: 'Review missing documents and document follow-up',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Document chase is a route-specific blocker and should be explicit on the documents tab.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_DOCUMENTS_ROUTE_PATTERNS,
    prompt: 'Review this family for missing or stale documents and explain the next document follow-up from the current tab.',
  }),
  capability({
    id: 'students-families-notes-summarize-activity-timeline',
    page: 'Students & Families',
    tab: 'Notes',
    sequence: 1,
    title: 'Summarize notes and recent activity timeline',
    type: 'insight',
    analysisScope: 'focused',
    rationale: 'Notes work is timeline-driven and benefits from one route-aware summary of recent activity and commitments.',
    module: 'students-families',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: STUDENTS_FAMILIES_NOTES_ROUTE_PATTERNS,
    prompt: 'Summarize the most important notes and recent activity timeline for this family, including any follow-up commitments.',
  }),
  capability({
    id: 'financial-aid-dashboard-estimate-budget-impact',
    page: 'Financial Aid',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Estimate budget impact',
    type: 'insight',
    method: 'For each pipeline family, estimate probable aid amount using family size, division tuition rates, and historical award-to-tuition ratios by stage. Sum probable awards, add to already-committed awards, and express as % of $2.5M budget against 80%/95% thresholds',
    rationale: 'Prevents late-stage budget surprises by forecasting commitments.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DASHBOARD_PREFIXES,
    prompt: 'Estimate financial aid budget impact from current pipeline and threshold exposure. If Annual Scholarship Budget, Warning Threshold (%), Critical Threshold (%), recommendation, decision award, or final award data is missing, give exact setup steps and navigation actions for Settings > Financial Aid > Budget or the application Contract & Flags / Income Calculation tabs.',
  }),
  capability({
    id: 'financial-aid-dashboard-prioritize-pending-reviews',
    page: 'Financial Aid',
    tab: 'Dashboard',
    sequence: 2,
    title: 'Prioritize pending reviews',
    type: 'insight',
    method: 'Calculate maximum possible aid exposure per family (children count x division tuition rate). Weight by application completeness % and stage proximity to decision. Rank descending by exposure x probability to produce a prioritized review queue',
    rationale: 'Focuses reviewers on highest financial risk first.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DASHBOARD_PREFIXES,
    prompt: 'Prioritize pending financial-aid reviews by exposure and readiness.',
  }),
  capability({
    id: 'financial-aid-dashboard-flag-stalled-applications',
    page: 'Financial Aid',
    tab: 'Dashboard',
    sequence: 3,
    title: 'Flag stalled applications',
    type: 'insight',
    method: 'Calculate days since last status change and days since last document upload or note for each card. Flag any application where both metrics exceed a configurable threshold (e.g. 30 days). Identify the specific blocker: missing docs, no contact, or awaiting review',
    rationale: 'Surfaces cases likely to miss deadlines without intervention.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DASHBOARD_PREFIXES,
    prompt: 'Flag stalled financial-aid applications and identify likely blockers.',
  }),
  capability({
    id: 'financial-aid-dashboard-compare-aid-by-family-size',
    page: 'Financial Aid',
    tab: 'Dashboard',
    sequence: 4,
    title: 'Compare aid by family size',
    type: 'insight',
    method: 'Group current financial-aid applications by child count, calculate projected aid coverage against tuition obligation, compare per-child aid ratios, and flag disproportionate groups or outliers.',
    rationale: 'Detects equity and consistency patterns in award outcomes.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DASHBOARD_PREFIXES,
    prompt: 'Compare current financial-aid projections by family size. Group by child count, show per-child and tuition-coverage differences, flag disproportionate groups or outliers, and state exactly what is missing if any records cannot be modeled.',
  }),
  capability({
    id: 'financial-aid-dashboard-forecast-remaining-capacity',
    page: 'Financial Aid',
    tab: 'Dashboard',
    sequence: 5,
    title: 'Forecast remaining capacity',
    type: 'insight',
    method: 'Compute remaining budget and estimate additional fundable families.',
    rationale: 'Guides decision pacing by remaining budget runway.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DASHBOARD_PREFIXES,
    prompt: 'Forecast remaining financial-aid capacity and estimated additional awards. If budget or award data is missing, guide the user to Settings > Financial Aid > Budget for Annual Scholarship Budget and thresholds, or to application Contract & Flags / Income Calculation for recommendation and committed award fields.',
  }),
  capability({
    id: 'financial-aid-detail-analyze-financial-consistency',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 1,
    title: 'Analyze financial consistency',
    type: 'insight',
    method: 'Compare hardship narrative against discretionary spending patterns.',
    rationale: 'Flags mismatches that affect confidence in aid recommendations.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Analyze financial consistency between hardship claims and discretionary spending.',
  }),
  capability({
    id: 'financial-aid-detail-explain-aid-calculation',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 2,
    title: 'Explain the aid calculation',
    type: 'insight',
    method: 'Trace calculation from gross income to adjusted aid basis and award percent.',
    rationale: 'Improves reviewer confidence and committee transparency.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Explain this aid calculation step-by-step from gross income to awarded amount.',
  }),
  capability({
    id: 'financial-aid-detail-assess-flag-severity',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 3,
    title: 'Assess flag severity',
    type: 'insight',
    method: 'Quantify flag metrics against thresholds and historical approval distribution.',
    rationale: 'Separates meaningful flags from low-impact noise.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Assess unresolved flag severity and likely impact on award recommendation.',
  }),
  capability({
    id: 'financial-aid-detail-verify-income-against-documents',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 4,
    title: 'Verify income against documents',
    type: 'insight',
    method: 'Cross-reference line items against uploaded verified documents.',
    rationale: 'Prevents unsupported income claims from driving decisions.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Verify income line items against supporting documents and flag missing evidence.',
  }),
  capability({
    id: 'financial-aid-detail-summarize-review-readiness',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 5,
    title: 'Summarize review readiness',
    type: 'insight',
    method: 'Score tab completeness and unresolved blockers into ready/not-ready output.',
    rationale: 'Supports fast reviewer handoff and decision readiness checks.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Summarize review readiness with pass/fail checklist across all tabs.',
  }),
  capability({
    id: 'financial-aid-detail-compare-tuition-to-ability',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 6,
    title: 'Compare tuition to ability',
    type: 'insight',
    method: 'Compute disposable income, surplus/deficit, and reserve runway.',
    rationale: 'Tests whether payment obligations are realistically sustainable.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Compare tuition obligation to family payment ability and reserve runway.',
  }),
  capability({
    id: 'financial-aid-detail-project-multi-year-sustainability',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 7,
    title: 'Project multi-year sustainability',
    type: 'insight',
    method: 'Model 3-year cash flow with tuition growth and reserve depletion.',
    rationale: 'Flags packages likely to fail over multiple years.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Project three-year sustainability for this aid package and family cash flow.',
  }),
  capability({
    id: 'financial-aid-detail-benchmark-against-similar-families',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 8,
    title: 'Benchmark against similar families',
    type: 'insight',
    method: 'Compare award percentile against anonymized matching cohort.',
    rationale: 'Detects outlier recommendations before committee approval.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Benchmark this application against similar families and flag outlier award percent.',
  }),
  capability({
    id: 'financial-aid-detail-identify-unreported-income-signals',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 9,
    title: 'Identify unreported income signals',
    type: 'insight',
    method: 'Infer minimum income from asset and lifestyle indicators.',
    rationale: 'Highlights likely underreported income risk.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Identify unreported income signals from assets and discretionary spending.',
  }),
  capability({
    id: 'financial-aid-detail-draft-committee-summary',
    page: 'Financial Aid',
    tab: 'Application Detail',
    sequence: 10,
    title: 'Draft committee summary',
    type: 'insight',
    method: 'Assemble structured cross-tab narrative for committee review.',
    rationale: 'Creates consistent final-review documentation format.',
    module: 'financial-aid',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCIAL_AID_DETAIL_PREFIXES,
    prompt: 'Draft a committee-ready financial aid summary with recommendation section.',
  }),
  capability({
    id: 'parent-portal-application-status-summary',
    page: 'Parent Portal',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Summarize application status',
    type: 'insight',
    method: 'Aggregate parent-visible application and enrollment state.',
    rationale: 'Parent persona needs immediate status clarity.',
    module: 'parent-portal',
    personas: PARENT_PERSONAS,
    routePrefixes: PARENT_PORTAL_PREFIXES,
    prompt: 'Summarize my family application and enrollment status.',
  }),
  capability({
    id: 'parent-portal-missing-documents-summary',
    page: 'Parent Portal',
    tab: 'Applications',
    sequence: 2,
    title: 'What is missing from my submissions',
    type: 'insight',
    method: 'List missing documents and unresolved checklist steps.',
    rationale: 'Parents need direct blockers to complete submissions.',
    module: 'parent-portal',
    personas: PARENT_PERSONAS,
    routePrefixes: PARENT_PORTAL_PREFIXES,
    prompt: 'What documents or steps are still missing for my submissions?',
  }),
  capability({
    id: 'parent-portal-upcoming-deadlines',
    page: 'Parent Portal',
    tab: 'Calendar',
    sequence: 3,
    title: 'Upcoming deadlines',
    type: 'insight',
    method: 'Summarize upcoming interviews, deadlines, and due dates.',
    rationale: 'Time-bound reminders are high-value for parent workflows.',
    module: 'parent-portal',
    personas: PARENT_PERSONAS,
    routePrefixes: PARENT_PORTAL_PREFIXES,
    prompt: 'Summarize upcoming deadlines and events for my family.',
  }),
  capability({
    id: 'parent-portal-financial-aid-status',
    page: 'Parent Portal',
    tab: 'Financial Aid',
    sequence: 4,
    title: 'Financial aid status summary',
    type: 'insight',
    method: 'Summarize aid review progress, requests, and pending actions.',
    rationale: 'Parents need clarity on aid timeline and next steps.',
    module: 'parent-portal',
    personas: PARENT_PERSONAS,
    routePrefixes: PARENT_PORTAL_PREFIXES,
    prompt: 'Summarize current financial aid status and any pending requests.',
  }),
  capability({
    id: 'attendance-overview-review-absence-trends',
    page: 'Attendance',
    tab: 'Overview',
    sequence: 1,
    title: 'Review absence trends',
    type: 'insight',
    method: 'Summarize attendance overview signals, records, and follow-up hotspots.',
    rationale: 'Attendance staff need a fast picture of current issues before acting on records.',
    module: 'attendance',
    personas: ATTENDANCE_STAFF_PERSONAS,
    routePrefixes: ATTENDANCE_OVERVIEW_ROUTE_PREFIXES,
    prompt: 'Summarize current attendance trends, risk hotspots, and likely follow-up needs.',
  }),
  capability({
    id: 'attendance-records-identify-follow-up-students',
    page: 'Attendance',
    tab: 'Records',
    sequence: 2,
    title: 'Identify students needing attendance follow-up',
    type: 'insight',
    method: 'Review records, student history, and exports to flag likely attendance follow-up.',
    rationale: 'Attendance records are used to decide which students need immediate staff outreach.',
    module: 'attendance',
    personas: ATTENDANCE_STAFF_PERSONAS,
    routePrefixes: ATTENDANCE_RECORDS_ROUTE_PREFIXES,
    prompt: 'Identify which students likely need attendance follow-up and explain why.',
  }),
  capability({
    id: 'attendance-teacher-view-review-preview',
    page: 'Attendance',
    tab: 'Teacher View',
    sequence: 4,
    title: 'Review teacher preview roster',
    type: 'insight',
    method: 'Summarize the preview-only teacher roster, status mix, and notes without suggesting writes.',
    rationale: 'Teacher View is preview-only in v1.2 and needs route-specific guidance.',
    module: 'attendance',
    personas: ATTENDANCE_STAFF_PERSONAS,
    routePrefixes: ['/school/attendance/teacher-view'],
    prompt: 'Review this teacher preview roster and identify any attendance follow-up without changing records.',
  }),
  capability({
    id: 'attendance-student-history-review-read-only',
    page: 'Attendance',
    tab: 'Student History',
    sequence: 5,
    title: 'Review read-only student history',
    type: 'insight',
    method: 'Summarize the 30-school-day student attendance history and point profile edits to Students & Families.',
    rationale: 'Student History is read-only and should not imply edits inside Attendance.',
    module: 'attendance',
    personas: ATTENDANCE_STAFF_PERSONAS,
    routePrefixes: ['/school/attendance/students'],
    prompt: 'Summarize this read-only student attendance history and identify likely follow-up.',
  }),
  capability({
    id: 'attendance-control-plane-review-config-audit',
    page: 'Attendance',
    tab: 'Control Plane',
    sequence: 6,
    title: 'Review attendance control plane',
    type: 'insight',
    method: 'Summarize control-plane config, import status, and audit events for super-administrator review.',
    rationale: 'Control Plane is super-administrator-only and needs config/import/audit-specific prompts.',
    module: 'attendance',
    personas: ['super-administrator'],
    routePrefixes: ['/super-administrator/control-plane/attendance'],
    prompt: 'Review attendance control-plane configuration, import status, and audit events for readiness risks.',
  }),
  capability({
    id: 'academics-summary-identify-gradebook-gaps',
    page: 'Academics',
    tab: 'Summary',
    sequence: 1,
    title: 'Identify gradebook gaps',
    type: 'insight',
    method: 'Summarize grade-entry coverage, approvals, and unresolved academic work.',
    rationale: 'Academic operators need a single pass over grade-entry and approval bottlenecks.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ACADEMICS_SUMMARY_ROUTE_PREFIXES,
    prompt: 'Summarize gradebook gaps, blocked approvals, and academic follow-up needs.',
  }),
  capability({
    id: 'academics-approvals-review-pending-work',
    page: 'Academics',
    tab: 'Approvals',
    sequence: 2,
    title: 'Review pending academic approvals',
    type: 'insight',
    method: 'Prioritize academic approvals and report-card follow-up from the current workflow.',
    rationale: 'Approval queues often drive the next staff action on academics routes.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: ACADEMICS_APPROVALS_ROUTE_PREFIXES,
    prompt: 'Review the pending academic approvals and recommend the next actions.',
  }),
  capability({
    id: 'substitutes-dashboard-prioritize-open-coverage',
    page: 'Substitutes',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Prioritize open substitute coverage',
    type: 'insight',
    method: 'Summarize absences, assignments, and urgent coverage gaps.',
    rationale: 'Coverage prioritization is the core operator need on substitute routes.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SUBSTITUTES_DASHBOARD_ROUTE_PREFIXES,
    prompt: 'Prioritize the most urgent substitute coverage gaps and explain what needs attention.',
  }),
  capability({
    id: 'substitutes-payroll-summarize-cost-and-follow-up',
    page: 'Substitutes',
    tab: 'Payroll',
    sequence: 2,
    title: 'Summarize substitute cost and follow-up',
    type: 'insight',
    method: 'Review payroll, assignments, and schedule context for substitute operations.',
    rationale: 'Payroll and assignment follow-up are frequent substitute-management tasks.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: SUBSTITUTES_PAYROLL_ROUTE_PREFIXES,
    prompt: 'Summarize substitute payroll and assignment follow-up risks from the current route.',
  }),
  capability({
    id: 'faculty-directory-summarize-staffing-coverage',
    page: 'Faculty',
    tab: 'Directory',
    sequence: 1,
    title: 'Summarize faculty staffing coverage',
    type: 'insight',
    method: 'Review faculty directory and profile context for staffing and communication follow-up.',
    rationale: 'Faculty routes center on directory awareness and profile-driven follow-up.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_DIRECTORY_ROUTE_PATTERNS,
    prompt: 'Summarize faculty staffing coverage, profile gaps, and communication follow-up.',
  }),
  capability({
    id: 'faculty-directory-guide-add-faculty-record',
    page: 'Faculty',
    tab: 'Directory',
    sequence: 2,
    title: 'Guide me through adding a faculty record',
    type: 'insight',
    method: 'Explain how to launch and complete the add-faculty workflow from the directory.',
    rationale: 'The add-faculty modal is a core directory workflow and should be available as route guidance.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_DIRECTORY_ROUTE_PATTERNS,
    prompt: 'Guide me through adding a faculty record from this directory, including the fields that should be checked before saving.',
  }),
  capability({
    id: 'faculty-profile-review-profile-summary',
    page: 'Faculty',
    tab: 'Profile',
    sequence: 1,
    title: 'Review this faculty profile',
    type: 'insight',
    method: 'Summarize faculty profile detail and highlight the most important operational context.',
    rationale: 'Profile overview is the first route-local read on faculty detail and should summarize the record clearly.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_PROFILE_ROUTE_PATTERNS,
    prompt: 'Review this faculty profile and summarize the most important overview details and follow-up context.',
  }),
  capability({
    id: 'faculty-profile-guide-edit-profile',
    page: 'Faculty',
    tab: 'Profile',
    sequence: 2,
    title: 'Guide me through editing this profile',
    type: 'insight',
    method: 'Explain the profile edit flow and which fields are most likely to need verification.',
    rationale: 'Editing the faculty record is a primary profile workflow and benefits from route-aware guidance.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_PROFILE_ROUTE_PATTERNS,
    prompt: 'Guide me through editing this faculty profile and explain which details should be verified before saving.',
  }),
  capability({
    id: 'faculty-teaching-review-assignment',
    page: 'Faculty',
    tab: 'Teaching',
    sequence: 1,
    title: 'Review this teaching assignment',
    type: 'insight',
    method: 'Summarize the current teaching assignment, rosters, and schedule context for this faculty member.',
    rationale: 'Teaching routes exist to review current assignment load and schedule context.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_TEACHING_ROUTE_PATTERNS,
    prompt: 'Review this teaching assignment and summarize the most important schedule, roster, and coverage context.',
  }),
  capability({
    id: 'faculty-notes-summarize-notes',
    page: 'Faculty',
    tab: 'Notes',
    sequence: 1,
    title: 'Summarize faculty notes',
    type: 'insight',
    method: 'Summarize recent faculty notes and any follow-up commitments.',
    rationale: 'Notes are a dedicated faculty workflow and need a route-specific summary.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_NOTES_ROUTE_PATTERNS,
    prompt: 'Summarize the most important faculty notes and any follow-up commitments on this route.',
  }),
  capability({
    id: 'faculty-notes-guide-add-note',
    page: 'Faculty',
    tab: 'Notes',
    sequence: 2,
    title: 'Guide me through adding a note',
    type: 'insight',
    method: 'Explain the note composer flow and what context should be captured.',
    rationale: 'Adding a note is a primary route-owned workflow on the notes tab.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_NOTES_ROUTE_PATTERNS,
    prompt: 'Guide me through adding a faculty note and explain what context should be captured before saving.',
  }),
  capability({
    id: 'faculty-communications-review-communications',
    page: 'Faculty',
    tab: 'Communications',
    sequence: 1,
    title: 'Review faculty communications',
    type: 'insight',
    method: 'Summarize recent faculty communications and any pending follow-up.',
    rationale: 'Communications routes are route-local review surfaces and need their own assistant summary.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_COMMUNICATIONS_ROUTE_PATTERNS,
    prompt: 'Review faculty communications on this route and summarize the most important messages and follow-up.',
  }),
  capability({
    id: 'faculty-communications-guide-compose-communication',
    page: 'Faculty',
    tab: 'Communications',
    sequence: 2,
    title: 'Guide me through composing a communication',
    type: 'insight',
    method: 'Explain how to compose a faculty communication from the current route and what to verify before sending.',
    rationale: 'Composing a communication is a primary route-owned task on the communications tab.',
    module: 'faculty',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FACULTY_COMMUNICATIONS_ROUTE_PATTERNS,
    prompt: 'Guide me through composing a communication from this faculty route and explain what to check before sending.',
  }),
  capability({
    id: 'finance-dashboard-summarize-revenue-mix',
    page: 'Finance',
    tab: 'Overview',
    sequence: 1,
    title: 'Summarize revenue mix',
    type: 'insight',
    method: 'Summarize revenue, cash flow, and current finance operations focus areas.',
    rationale: 'Finance operators need immediate clarity on revenue and cash movement.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCE_OVERVIEW_ROUTE_PREFIXES,
    prompt: 'Summarize the current finance picture across revenue, cash flow, and key drivers.',
  }),
  capability({
    id: 'finance-reconciliation-flag-ledger-gaps',
    page: 'Finance',
    tab: 'Reconciliation',
    sequence: 2,
    title: 'Flag ledger gaps',
    type: 'insight',
    method: 'Review reconciliation, journals, and export workflows for gaps or follow-up.',
    rationale: 'Ledger and reconciliation gaps drive the next finance action more than static totals.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: FINANCE_RECONCILIATION_ROUTE_PREFIXES,
    prompt: 'Flag reconciliation or ledger gaps that need attention from the current finance route.',
  }),
  capability({
    id: 'payroll-dashboard-summarize-readiness',
    page: 'Payroll',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Summarize payroll readiness',
    type: 'insight',
    method: 'Review pay-period readiness, approvals, export status, and payroll follow-up for the current route.',
    rationale: 'Payroll operators need route-local visibility into pay-period readiness before approving or exporting.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: PAYROLL_DASHBOARD_ROUTE_PREFIXES,
    prompt: 'Summarize pay-period readiness, approval gaps, export status, and follow-up needs from the current payroll route.',
  }),
  capability({
    id: 'payroll-employee-review-compensation-detail',
    page: 'Payroll',
    tab: 'Employee Detail',
    sequence: 2,
    title: 'Review employee payroll detail',
    type: 'insight',
    method: 'Summarize employee payroll detail, compensation, stipends, deductions, and approval follow-up.',
    rationale: 'Employee payroll detail routes should provide an immediate payroll-specific review starter.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: PAYROLL_EMPLOYEE_DETAIL_ROUTE_PREFIXES,
    prompt: 'Review this employee payroll detail and summarize compensation, stipends, deductions, approvals, and follow-up.',
  }),
  capability({
    id: 'camps-dashboard-summarize-enrollment-and-capacity',
    page: 'Camps',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Summarize camp enrollment and capacity',
    type: 'insight',
    method: 'Review camp programs, registrations, and waitlist pressure from the current route.',
    rationale: 'Enrollment and capacity are the highest-signal camp operations questions.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: CAMPS_DASHBOARD_ROUTE_PREFIXES,
    prompt: 'Summarize camp enrollment, capacity pressure, and operational follow-up.',
  }),
  capability({
    id: 'camps-dashboard-review-legacy-sheet-sync-health',
    page: 'Camps',
    tab: 'Dashboard',
    sequence: 2,
    title: 'Review legacy sheet sync health',
    type: 'insight',
    method: 'Inspect legacy-sheet authority, GF export freshness, and camps-domain mirror status for the current route.',
    rationale: 'Operators need to confirm the sheet-backed legacy lane is fresh after GF export and AWS cutover changes.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: CAMPS_DASHBOARD_ROUTE_PREFIXES,
    prompt: 'Review the legacy camps sheet sync health, freshness, and mirror status for this route.',
  }),
  capability({
    id: 'camps-detail-review-registration-readiness',
    page: 'Camps',
    tab: 'Program Detail',
    sequence: 2,
    title: 'Review registration readiness',
    type: 'insight',
    method: 'Summarize registration, waitlist, and program detail readiness for the selected camp.',
    rationale: 'Camp builders and detail pages are used to determine what to change next.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: CAMPS_PROGRAM_DETAIL_ROUTE_PREFIXES,
    prompt: 'Review registration readiness for this camp and call out likely blockers.',
  }),
  capability({
    id: 'donations-dashboard-summarize-campaign-performance',
    page: 'Donations',
    tab: 'Dashboard',
    sequence: 1,
    title: 'Summarize campaign performance',
    type: 'insight',
    method: 'Review campaigns, ledger activity, and moderation context for the current route.',
    rationale: 'Campaign performance and follow-up are the main donation operator workflows.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: DONATIONS_DASHBOARD_ROUTE_PREFIXES,
    prompt: 'Summarize current donation campaign performance and follow-up priorities.',
  }),
  capability({
    id: 'donations-ledger-review-moderation-and-follow-up',
    page: 'Donations',
    tab: 'Ledger',
    sequence: 2,
    title: 'Review donation ledger and moderation follow-up',
    type: 'insight',
    method: 'Summarize ledger, moderation, and settings follow-up from the current donation route.',
    rationale: 'Donation staff often need one answer spanning ledger state and moderation tasks.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: DONATIONS_LEDGER_ROUTE_PREFIXES,
    prompt: 'Review the donation ledger and moderation queue, then recommend next actions.',
  }),
  capability({
    id: 'tip-a-teacher-summarize-program-status',
    page: 'Tip a Teacher',
    tab: 'Overview',
    sequence: 1,
    title: 'Summarize teacher tip program status',
    type: 'insight',
    method: 'Review teacher tip totals, program status, and recent activity from the current route.',
    rationale: 'Tip a Teacher is a staff-facing route and must expose assistant route starts like the other staff program routes.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: TIP_A_TEACHER_ROUTE_PREFIXES,
    prompt: 'Summarize the teacher tip program status, totals, and follow-up priorities.',
  }),
  capability({
    id: 'tip-a-teacher-review-settings-and-notifications',
    page: 'Tip a Teacher',
    tab: 'Settings',
    sequence: 2,
    title: 'Review settings and notifications',
    type: 'insight',
    method: 'Review Tip a Teacher settings, notification readiness, and missing configuration.',
    rationale: 'Staff need one route-local assistant entry point for settings and notification follow-up.',
    module: 'general',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: TIP_A_TEACHER_ROUTE_PREFIXES,
    prompt: 'Review Tip a Teacher settings and notification readiness, then call out missing configuration.',
  }),
  capability({
    id: 'tuition-dashboard-summarize-account-risk',
    page: 'Tuition',
    tab: 'Accounts',
    sequence: 1,
    title: 'Summarize tuition account risk',
    type: 'insight',
    method: 'Review tuition accounts, billing context, and payment follow-up risk.',
    rationale: 'Account risk and next actions are the primary tuition questions.',
    module: 'tuition',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: TUITION_ACCOUNTS_ROUTE_PREFIXES,
    prompt: 'Summarize tuition account risk, payment pressure, and likely follow-up needs.',
  }),
  capability({
    id: 'tuition-operations-review-billing-actions',
    page: 'Tuition',
    tab: 'Operations',
    sequence: 2,
    title: 'Review billing actions',
    type: 'insight',
    method: 'Summarize billing operations, adjustments, and administrator follow-up.',
    rationale: 'Billing operations require route-aware next-step guidance, not just static totals.',
    module: 'tuition',
    personas: STAFF_INSIGHT_PERSONAS,
    routePrefixes: TUITION_OPERATIONS_ROUTE_PREFIXES,
    prompt: 'Review current billing actions and explain what needs attention next.',
  }),
];

const normalizeToken = (value: string): string =>
  value.trim().toLowerCase().replace(/[_\s]+/g, '-');

const PROMPT_MATRIX_SOURCE_BY_KEY = new Map(
  YSTNJ_ASSISTANT_PROMPT_MATRIX_SOURCE_ROWS.map((row) => [
    buildAssistantPromptMatrixSourceKey({
      page: row.page,
      tab: row.tab,
      sequence: row.sequence,
      prompt: row.prompt,
    }),
    row,
  ] as const)
);

const toOptionalText = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
};

const normalizeAssistantPathname = (value: string | null | undefined): string => {
  const raw = String(value ?? '').trim();
  const withLeadingSlash = raw.startsWith('/') ? raw : `/${raw}`;
  const withoutQuery = withLeadingSlash.split('?')[0] ?? withLeadingSlash;
  const collapsed = withoutQuery.replace(/\/{2,}/g, '/').toLowerCase();
  if (collapsed.length > 1 && collapsed.endsWith('/')) {
    return collapsed.slice(0, -1);
  }
  return collapsed;
};

export const normalizeAssistantPersona = (value: string | null | undefined): string => {
  const normalized = normalizeToken(value ?? '');
  if (normalized === 'administrator') return 'admin';
  if (normalized === 'super-administrator') return 'super-administrator';
  return normalized;
};

const resolvePromptMatrixSourceRowForCapability = (
  capability: AssistantCapabilityDefinition
) => PROMPT_MATRIX_SOURCE_BY_KEY.get(buildAssistantPromptMatrixSourceKey({
  page: capability.page,
  tab: capability.tab,
  sequence: capability.sequence,
  prompt: capability.title,
})) ?? null;

export const resolveAssistantCapabilityScenarioKey = (
  capability: AssistantCapabilityDefinition
): string => {
  const sourceRow = resolvePromptMatrixSourceRowForCapability(capability);
  return buildAssistantPromptMatrixScenarioKey({
    page: sourceRow?.page ?? capability.page,
    tab: sourceRow?.tab ?? capability.tab,
  });
};

export const resolveAssistantModuleFromPath = (pathname: string | null | undefined): AssistantModuleKey => {
  const normalizedPath = normalizeAssistantPathname(pathname);
  if (normalizedPath.startsWith('/school/admissions')) {
    return 'admissions';
  }
  if (normalizedPath.startsWith('/school/students')) {
    return 'students-families';
  }
  if (
    normalizedPath.startsWith('/school/faculty')
    || normalizedPath.startsWith('/admin/faculty')
    || normalizedPath.startsWith('/bridged/admin/faculty')
  ) {
    return 'faculty';
  }
  if (normalizedPath.startsWith('/school/financialaid') || normalizedPath.startsWith('/school/financial-aid')) {
    return 'financial-aid';
  }
  if (normalizedPath.startsWith('/school/tuition') || normalizedPath.startsWith('/administrator/tuition')) {
    return 'tuition';
  }
  if (normalizedPath.startsWith('/school/settings')) {
    return 'settings';
  }
  if (
    normalizedPath.startsWith('/school/attendance')
    || normalizedPath.startsWith('/super-administrator/control-plane/attendance')
  ) {
    return 'attendance';
  }
  if (normalizedPath.startsWith('/parent/portal') || normalizedPath.startsWith('/portal/family')) {
    return 'parent-portal';
  }
  return 'general';
};

const routeMatchesCapability = (pathname: string, capability: AssistantCapabilityDefinition): boolean => {
  if (capability.routePrefixes.length === 0) {
    return true;
  }
  return routeMatchesAnyAssistantPattern(pathname, capability.routePrefixes);
};

const resolveCapabilityRouteSpecificityScore = (
  pathname: string,
  capability: AssistantCapabilityDefinition,
): number => {
  if (capability.routePrefixes.length === 0) {
    return 0;
  }
  return capability.routePrefixes.reduce((best, pattern) => {
    if (!routeMatchesAssistantPattern(pathname, pattern)) {
      return best;
    }
    const rawPattern = stripAssistantRoutePatternQualifier(pattern).trim();
    const normalizedPattern = normalizeAssistantRouteIdentity(rawPattern);
    const descendantOnlyBonus = rawPattern.endsWith('/') ? 2 : 0;
    const exactBonus = pattern.trim().startsWith('exact:') ? 4 : 0;
    return Math.max(best, normalizedPattern.length + descendantOnlyBonus + exactBonus);
  }, 0);
};

const resolveCapabilityRouteFamilyScore = (
  routeFamilyId: AssistantRouteFamilyId,
  capability: AssistantCapabilityDefinition,
): number => (
  capability.routePrefixes.some((pattern) => (
    resolveAssistantRouteFamily(stripAssistantRoutePatternQualifier(pattern)).id === routeFamilyId
  ))
    ? 1
    : 0
);

export const listAssistantCapabilitiesForContext = (input: {
  pathname?: string | null;
  persona?: string | null;
  module?: AssistantModuleKey | null;
  types?: AssistantCapabilityType[];
  strictRouteMatch?: boolean;
  catalogScope?: AssistantCapabilityCatalogScope;
}): AssistantCapabilityDefinition[] => {
  const pathname = normalizeAssistantPathname(input.pathname);
  const persona = normalizeAssistantPersona(input.persona);
  const routeModule = input.module ?? resolveAssistantModuleFromPath(pathname);
  const routeFamilyId = resolveAssistantRouteFamily(pathname).id;
  const typeFilter = input.types && input.types.length > 0 ? new Set(input.types) : null;
  const catalogScope = input.catalogScope ?? 'route';
  const strictRouteMatch = catalogScope === 'route' ? (input.strictRouteMatch ?? true) : false;
  const module = catalogScope === 'staff-global' ? null : routeModule;

  return YSTNJ_ASSISTANT_CAPABILITY_MATRIX
    .filter((entry) => (module ? entry.module === module : entry.personas.includes('parent') === false))
    .filter((entry) => entry.personas.includes(persona as AssistantPersona))
    .filter((entry) => (strictRouteMatch ? routeMatchesCapability(pathname, entry) : true))
    .filter((entry) => (typeFilter ? typeFilter.has(entry.type) : true))
    .sort((left, right) => {
      const leftRouteScore = routeMatchesCapability(pathname, left) ? 1 : 0;
      const rightRouteScore = routeMatchesCapability(pathname, right) ? 1 : 0;
      const leftRouteSpecificity = resolveCapabilityRouteSpecificityScore(pathname, left);
      const rightRouteSpecificity = resolveCapabilityRouteSpecificityScore(pathname, right);
      const leftRouteFamilyScore = resolveCapabilityRouteFamilyScore(routeFamilyId, left);
      const rightRouteFamilyScore = resolveCapabilityRouteFamilyScore(routeFamilyId, right);
      const leftModuleScore = left.module === routeModule ? 1 : 0;
      const rightModuleScore = right.module === routeModule ? 1 : 0;
      return (
        rightRouteScore - leftRouteScore
        || rightRouteSpecificity - leftRouteSpecificity
        || rightRouteFamilyScore - leftRouteFamilyScore
        || rightModuleScore - leftModuleScore
        || left.sequence - right.sequence
        || left.title.localeCompare(right.title)
      );
    });
};

export const resolveAssistantCapabilityById = (
  capabilityId: string
): AssistantCapabilityDefinition | null => (
  YSTNJ_ASSISTANT_CAPABILITY_MATRIX.find((entry) => entry.id === capabilityId) ?? null
);

const formatCapabilityTargetLabel = (
  capability: AssistantCapabilityDefinition | null,
  isSettingsSpecialist: boolean,
): string | null => {
  if (capability) {
    return capability.tab
      ? `${capability.page} ${capability.tab}`
      : capability.page;
  }
  if (isSettingsSpecialist) {
    return 'Settings';
  }
  return null;
};

const buildBindingValidationResult = (input: {
  hasBinding: boolean;
  isValid: boolean;
  reasonCode: AssistantCapabilityBindingValidationReasonCode;
  routeModule: AssistantModuleKey;
  capability: AssistantCapabilityDefinition | null;
  scenarioKey?: string | null;
  method?: string | null;
  isSettingsSpecialist?: boolean;
}): AssistantCapabilityBindingValidationResult => ({
  hasBinding: input.hasBinding,
  isValid: input.isValid,
  reasonCode: input.reasonCode,
  routeModule: input.routeModule,
  capability: input.capability,
  scenarioKey: input.scenarioKey ?? null,
  method: input.method ?? null,
  targetLabel: formatCapabilityTargetLabel(input.capability, input.isSettingsSpecialist === true),
  isSettingsSpecialist: input.isSettingsSpecialist === true,
});

export const validateAssistantCapabilityBindingForRoute = (input: {
  pathname?: string | null;
  persona?: string | null;
  capabilityId?: string | null;
  scenarioKey?: string | null;
  method?: string | null;
}): AssistantCapabilityBindingValidationResult => {
  const pathname = normalizeAssistantPathname(input.pathname);
  const persona = normalizeAssistantPersona(input.persona);
  const routeContextIsAuthority = persona === 'parent';
  const routeModule = resolveAssistantModuleFromPath(pathname);
  const capabilityId = toOptionalText(input.capabilityId);
  const scenarioKey = toOptionalText(input.scenarioKey);
  const method = toOptionalText(input.method);
  const hasBinding = Boolean(capabilityId || scenarioKey || method);
  const isSettingsSpecialist = hasExplicitSettingsSpecialistBinding({
    activeCapabilityId: capabilityId,
    activeCapabilityMethod: method,
  });

  if (!hasBinding) {
    return buildBindingValidationResult({
      hasBinding: false,
      isValid: true,
      reasonCode: 'no-binding',
      routeModule,
      capability: null,
    });
  }

  if (isSettingsSpecialist) {
    return buildBindingValidationResult({
      hasBinding: true,
      isValid: routeModule === 'settings',
      reasonCode: routeModule === 'settings' ? 'aligned' : 'capability-route-mismatch',
      routeModule,
      capability: null,
      scenarioKey,
      method,
      isSettingsSpecialist: true,
    });
  }

  const routeCapabilities = listAssistantCapabilitiesForContext({
    pathname,
    persona,
    module: routeModule,
    strictRouteMatch: true,
    catalogScope: 'route',
  });
  const routeCapabilityById = capabilityId
    ? (routeCapabilities.find((entry) => entry.id === capabilityId) ?? null)
    : null;

  if (capabilityId) {
    const capability = resolveAssistantCapabilityById(capabilityId);
    if (!capability) {
      return buildBindingValidationResult({
        hasBinding: true,
        isValid: false,
        reasonCode: 'capability-not-found',
        routeModule,
        capability: null,
        scenarioKey,
        method,
      });
    }

	    const resolvedScenarioKey = resolveAssistantCapabilityScenarioKey(capability);
	    const resolvedMethod = capability.method ?? null;
	    if (!routeCapabilityById) {
	      if (!routeContextIsAuthority) {
	        if (scenarioKey && scenarioKey !== resolvedScenarioKey) {
	          return buildBindingValidationResult({
	            hasBinding: true,
	            isValid: false,
	            reasonCode: 'capability-route-mismatch',
	            routeModule,
	            capability,
	            scenarioKey: resolvedScenarioKey,
	            method: resolvedMethod,
	          });
	        }
	        if (method && resolvedMethod && method !== resolvedMethod) {
	          return buildBindingValidationResult({
	            hasBinding: true,
	            isValid: false,
	            reasonCode: 'capability-route-mismatch',
	            routeModule,
	            capability,
	            scenarioKey: resolvedScenarioKey,
	            method: resolvedMethod,
	          });
	        }
	        return buildBindingValidationResult({
	          hasBinding: true,
	          isValid: true,
	          reasonCode: 'aligned',
	          routeModule,
	          capability,
	          scenarioKey: resolvedScenarioKey,
	          method: resolvedMethod,
	        });
	      }
	      return buildBindingValidationResult({
	        hasBinding: true,
	        isValid: false,
        reasonCode: 'capability-route-mismatch',
        routeModule,
        capability,
        scenarioKey: resolvedScenarioKey,
        method: resolvedMethod,
      });
    }

    if (scenarioKey && scenarioKey !== resolveAssistantCapabilityScenarioKey(routeCapabilityById)) {
      return buildBindingValidationResult({
        hasBinding: true,
        isValid: false,
        reasonCode: 'capability-route-mismatch',
        routeModule,
        capability: routeCapabilityById,
        scenarioKey: resolveAssistantCapabilityScenarioKey(routeCapabilityById),
        method: routeCapabilityById.method ?? null,
      });
    }

    if (method && routeCapabilityById.method && method !== routeCapabilityById.method) {
      return buildBindingValidationResult({
        hasBinding: true,
        isValid: false,
        reasonCode: 'capability-route-mismatch',
        routeModule,
        capability: routeCapabilityById,
        scenarioKey: resolveAssistantCapabilityScenarioKey(routeCapabilityById),
        method: routeCapabilityById.method ?? null,
      });
    }

    return buildBindingValidationResult({
      hasBinding: true,
      isValid: true,
      reasonCode: 'aligned',
      routeModule,
      capability: routeCapabilityById,
      scenarioKey: resolveAssistantCapabilityScenarioKey(routeCapabilityById),
      method: routeCapabilityById.method ?? null,
    });
  }

	  const matchedByScenario = scenarioKey
	    ? (routeCapabilities.find((entry) => resolveAssistantCapabilityScenarioKey(entry) === scenarioKey) ?? null)
	    : null;
	  const matchedByMethod = method
	    ? (routeCapabilities.find((entry) => entry.method === method) ?? null)
	    : null;
	  const staffGlobalCapabilities = routeContextIsAuthority
	    ? []
	    : listAssistantCapabilitiesForContext({
	        pathname,
	        persona,
	        catalogScope: 'staff-global',
	      });
	  const matchedStaffGlobalByScenario = scenarioKey
	    ? (staffGlobalCapabilities.find((entry) => resolveAssistantCapabilityScenarioKey(entry) === scenarioKey) ?? null)
	    : null;
	  const matchedStaffGlobalByMethod = method
	    ? (staffGlobalCapabilities.find((entry) => entry.method === method) ?? null)
	    : null;
	  const matchedCapability =
	    matchedByScenario ??
	    matchedByMethod ??
	    matchedStaffGlobalByScenario ??
	    matchedStaffGlobalByMethod;

  if (!matchedCapability) {
    return buildBindingValidationResult({
      hasBinding: true,
      isValid: false,
      reasonCode: 'capability-route-mismatch',
      routeModule,
      capability: null,
      scenarioKey,
      method,
    });
  }

  const resolvedScenarioKey = resolveAssistantCapabilityScenarioKey(matchedCapability);
  const resolvedMethod = matchedCapability.method ?? null;
	  if ((scenarioKey && scenarioKey !== resolvedScenarioKey) || (method && resolvedMethod && method !== resolvedMethod)) {
    return buildBindingValidationResult({
      hasBinding: true,
      isValid: false,
      reasonCode: 'capability-route-mismatch',
      routeModule,
      capability: matchedCapability,
      scenarioKey: resolvedScenarioKey,
      method: resolvedMethod,
    });
  }

  return buildBindingValidationResult({
    hasBinding: true,
    isValid: true,
    reasonCode: 'aligned',
    routeModule,
    capability: matchedCapability,
    scenarioKey: resolvedScenarioKey,
    method: resolvedMethod,
  });
};
